/* eslint-disable react/jsx-max-props-per-line */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { Grid, Typography } from '@mui/material';
import moment from 'moment';
import validate from 'validate.js';
import {
  DialogDefaultV2,
  DateTimePickerV2,
  InputV2,
  InputFormatV2,
  DefaultSelectV2,
  ArquivoContainerV2,
} from '../../../../components/v2';

import * as selectorsAuth from '../../../../store/auth/reducer';
import * as selectorsNsir from '../../../../store/nsir/reducer';
import * as selectorsNsmp from '../../../../store/nsmp/reducer';
import * as actionsNsmp from '../../../../store/nsmp/actions';
import * as Role from '../../../../constants/role';
import * as Status from '../../../../constants/status';
import { isUsoMutuo, isUsuarioUsoMuto } from 'utils';

const rootGridSx = { pt: 1 };
const fieldContainerSx = { pb: 0 };
const posteContainerSx = {
  m: 1.5,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  pb: 1.25,
};
const posteContainerTitleSx = {
  bgcolor: theme => theme.palette.neutral[900],
  color: 'common.white',
  fontSize: 14,
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
};
const requiredTextSx = { color: 'error.main', pt: 0.5 };

const UsoMutuoModal = ({
  open,
  onCancel,
  onConfirm,
  selectedId,
  nsmps,
  nsmpSelected,
  loadNsmpById,
  readOnly,
  userRole,
  authData,
  nsirStatus,
  nsirData,
  nsirTitle,
  digitadorId,
  userId,
}) => {
  const [canEditNsmp, setCanEditNsmp] = useState(false);
  const [readOnlyLocal, setReadOnlyLocal] = useState(readOnly);
  const [editFiles, setEditFiles] = useState(false);
  const [errors, setErrors] = useState({});
  const [values, setValues] = useState({
    dataFotoDrone: moment().valueOf(),
    fotos: [],
  });

  useEffect(() => {
    setReadOnlyLocal(readOnly);
  }, [readOnly]);

  useEffect(() => {
    if (selectedId) loadNsmpById(selectedId);
  }, [selectedId, loadNsmpById]);

  useEffect(() => {
    if (nsmpSelected && nsmpSelected.id) {
      setValues(nsmpSelected);
    }
  }, [nsmpSelected]);

  const setSchema = fields => {
    let schema = {};
    fields.forEach(f => {
      schema = {
        ...schema,
        [f]: {
          presence: { allowEmpty: false, message: 'é obrigatório' },
        },
      };
    });
    return schema;
  };

  const validateCoordenadas = data => {
    let localErrors = {};

    if (nsirStatus === Status.EM_DIGITACAO) {
      if (data.tipoServico === 'NC') {
        return localErrors;
      }

      if (data.latitude && (data.latitude.trim().length !== 9 || !data.latitude.match(/\d{2}(,\d{6})?/))) {
        localErrors = { ...localErrors, latitude: ['O Campo é inválido'] };
      }

      if (data.longitude && (data.longitude.trim().length !== 9 || !data.longitude.match(/\d{2}(,\d{6})?/))) {
        localErrors = { ...localErrors, longitude: ['O Campo é inválido'] };
      }

      if (data.coordenada && (data.coordenada.trim().length !== 14 || !data.coordenada.match(/\d{5}(:\d{7})?/))) {
        localErrors = { ...localErrors, coordenada: ['O Campo é inválido'] };
      }

      if ((!data.latitude || !data.longitude) && !data.coordenada) {
        localErrors = {
          ...localErrors,
          latitude: ['O Campo é obrigatório'],
          longitude: ['O Campo é obrigatório'],
          coordenada: ['O Campo é obrigatório'],
        };
      }
    }

    return localErrors;
  };

  const getSchema = () => {
    let schema = ['prioridade', 'acesso', 'servico', 'acessoVeiculo', 'equipeIndicada', 'fotos'];

    if (nsirData.area === 'RDU') {
      schema.push('endereco');
    }

    if (nsirStatus === Status.EM_DIGITACAO) {
      schema = ['tipoServico'];
      if (values.tipoServico === 'PL') {
        schema.push('numLinhasPlanilha');
      } else if (values.tipoServico === 'PR') {
        schema.push('nsmpProgramada');
      }

      if (values.tipoServico === 'NC') {
        schema.push('motivo');
      } else if (values.tipoServico !== 'NC' && values.tipoServico !== 'PR') {
        schema.push('nsmp');
      }
    }

    return setSchema(schema);
  };

  const handleChange = (id, value) => {
    setValues({
      ...values,
      [id]: value,
    });
  };

  const clearForm = () => {
    setCanEditNsmp(false);
    setEditFiles(false);
    setErrors({});
    setValues({
      fotos: [],
      dataFotoDrone: moment().valueOf(),
    });
  };

  const onCancelLocal = () => {
    if (canEditNsmp) {
      setCanEditNsmp(false);
      setEditFiles(false);
      setErrors({});
      setReadOnlyLocal(true);
      if (nsmpSelected && nsmpSelected.id) {
        setValues(nsmpSelected);
      }
      return;
    }
    clearForm();
    onCancel();
  };

  const getDataFotoDrone = () => {
    let data = moment().valueOf();

    if (values.dataFotoDrone) {
      data = values.dataFotoDrone;
    }

    return moment(data).valueOf();
  };

  const onConfirmLocal = () => {
    let validationErrors = { ...validate(values, getSchema()) };
    if (nsirStatus === Status.EM_DIGITACAO) {
      validationErrors = { ...validationErrors, ...validateCoordenadas(values) };
    }

    setErrors(validationErrors);
    if (!validationErrors || (validationErrors && Object.keys(validationErrors).length === 0)) {
      const updateFiles = editFiles;
      clearForm();
      onConfirm({
        ...values,
        codigo: getCodigo(),
        dataFotoDrone: getDataFotoDrone(),
        updateFiles,
      });
    }
  };

  const getCodigo = () => {
    if (values && values.id) return values.codigo;

    let codigo = Array.isArray(nsmps) && nsmps.length > 0
      ? nsmps.filter(n => n.fotoInicio !== true && n.fotoFim !== true && n.analiseRisco !== true).length + 1
      : 1;
    while ((codigo + '').length < 3) codigo = `0${codigo}`;
    return codigo;
  };

  const isAdmin = () => userRole === Role.ADMIN || userRole === Role.SUPER_ADMIN;

  const getTitle = () => {
    if (userRole === Role.INSPETOR && !selectedId) {
      return `${nsirTitle} > Novo Serviço ${values.codigo}`;
    }
    if (values.nsmp) {
      return `${nsirTitle} > Serviço ${values.codigo} > NSMP ${values.nsmp}`;
    }
    return `${nsirTitle} > Serviço ${values.codigo}`;
  };

  const disabledEditDigitador = () =>
    digitadorId !== userId || nsirStatus !== Status.EM_DIGITACAO || isUsuarioUsoMuto(authData.email);

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onCancelLocal}
      handlerConfirm={onConfirmLocal}
      title={getTitle()}
      confirmMessage="Salvar"
      fullScreen
      readOnly={canEditNsmp ? false : nsirStatus === Status.EM_DIGITACAO ? false : readOnlyLocal}
      headerAction={!isUsuarioUsoMuto(authData.email) && isAdmin() && readOnlyLocal ? {
        onClick: () => {
          setReadOnlyLocal(false);
          setCanEditNsmp(true);
        },
        title: 'Editar',
      } : null}
    >
      <Grid container spacing={1} sx={rootGridSx}>
        <>
          <Grid size={{ xs: 12, sm: 1 }} sx={fieldContainerSx}>
            <InputV2 name="codigo" label="Código" disabled value={getCodigo()} />
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }} sx={fieldContainerSx}>
            <DateTimePickerV2
              onChange={data => handleChange('dataFotoDrone', data.valueOf())}
              label="Data da Foto Drone"
              value={values.dataFotoDrone || moment().valueOf()}
              disabled={readOnlyLocal}
            />
          </Grid>
          <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
            <InputV2
              error={!!(errors && errors.servico)}
              isRequired
              multiline
              rows={2}
              label="Serviço"
              name="servico"
              onChange={event => handleChange(event.target.name, event.target.value)}
              value={values.servico}
              disabled={readOnlyLocal}
            />
          </Grid>
        </>
        {(Role.DIGITADOR === userRole || Role.ADMIN === userRole) && nsirStatus !== Status.EM_INSPECAO && (
          <Grid container spacing={1} sx={posteContainerSx}>
            <Grid size={{ xs: 12 }} sx={posteContainerTitleSx}>
              Informações da NSMP
            </Grid>
            <Grid size={{ xs: 3 }} sx={fieldContainerSx}>
              <DefaultSelectV2
                error={!!(errors && errors.tipoServico)}
                isRequired={isAdmin() ? false : nsirStatus === Status.EM_DIGITACAO}
                label="Tipo de Serviço"
                name="tipoServico"
                showEmpty
                options={[
                  { id: 'E', nome: 'E-mail' },
                  { id: 'EM', nome: 'Emergência' },
                  { id: 'L', nome: 'Limpeza de Faixa' },
                  { id: 'M', nome: 'Manutenção' },
                  { id: 'NC', nome: 'Não Cadastrada' },
                  { id: 'OB', nome: 'Objeto na Rede' },
                  { id: 'OS', nome: 'OSAF' },
                  { id: 'PL', nome: 'Planilha' },
                  { id: 'P', nome: 'Poda RDR ou RDU' },
                  { id: 'PR', nome: 'Programada' },
                ]}
                onSelect={event => {
                  setValues({
                    ...values,
                    [event.target.name]: event.target.value || null,
                    numLinhasPlanilha: null,
                    motivo: null,
                    nsmp: event.target.value === 'E' ? 'E-MAIL' : event.target.value === 'EM' ? 'EMERGÊNCIA' : event.target.value === 'OS' ? 'OSAF' : '',
                  });
                }}
                value={values.tipoServico}
                disabled={disabledEditDigitador() || (canEditNsmp ? false : nsirStatus !== Status.EM_DIGITACAO)}
              />
            </Grid>
            <Grid size={{ xs: 3 }} sx={fieldContainerSx}>
              <DefaultSelectV2
                error={!!(errors && errors.motivo)}
                isRequired={nsirStatus === Status.EM_DIGITACAO && values.tipoServico === 'NC'}
                label="Motivo"
                name="motivo"
                showEmpty
                options={[
                  { id: 'NCA', nome: 'Anotação Indevida' },
                  { id: 'NCD', nome: 'Duplicidade' },
                  { id: 'NCP', nome: 'Prioridade' },
                  { id: 'NCPL', nome: 'Planilha' },
                ]}
                onSelect={event => {
                  setValues({
                    ...values,
                    [event.target.name]: event.target.value || null,
                  });
                }}
                value={values.motivo}
                disabled={disabledEditDigitador() || (canEditNsmp ? values.tipoServico !== 'NC' : nsirStatus !== Status.EM_DIGITACAO || (nsirStatus === Status.EM_DIGITACAO && values.tipoServico !== 'NC'))}
              />
            </Grid>
            <Grid size={{ xs: 3 }} sx={fieldContainerSx}>
              <InputV2
                error={!!(errors && errors.numLinhasPlanilha)}
                isRequired={values.tipoServico === 'PL'}
                name="numLinhasPlanilha"
                label="N° Linhas"
                value={values.numLinhasPlanilha}
                onChange={event => handleChange(event.target.name, event.target.value)}
                disabled={disabledEditDigitador() || (canEditNsmp ? values.tipoServico !== 'PL' : (nsirStatus !== Status.EM_DIGITACAO || (nsirStatus === Status.EM_DIGITACAO && values.tipoServico !== 'PL')))}
              />
            </Grid>
            <Grid size={{ xs: 3 }} sx={fieldContainerSx}>
              <InputV2
                error={!!(errors && errors.nsmpProgramada)}
                isRequired={values.tipoServico === 'PR'}
                name="nsmpProgramada"
                label="N° Nsmp Programada"
                value={values.nsmpProgramada}
                onChange={event => handleChange(event.target.name, event.target.value)}
                disabled={disabledEditDigitador() || (canEditNsmp ? false : nsirStatus !== Status.EM_DIGITACAO)}
              />
            </Grid>
            <Grid size={{ xs: 3 }} sx={fieldContainerSx}>
              <InputV2
                error={!!(errors && errors.nsmp)}
                isRequired={nsirStatus === Status.EM_DIGITACAO && values.tipoServico !== 'NC' && values.tipoServico !== 'PR'}
                name="nsmp"
                label="N° Nsmp Nova"
                value={values.nsmp}
                onChange={event => handleChange(event.target.name, event.target.value)}
                disabled={disabledEditDigitador() || (canEditNsmp
                  ? values.tipoServico === 'E' || values.tipoServico === 'EM' || values.tipoServico === 'OS' || values.tipoServico === 'PR'
                  : (nsirStatus !== Status.EM_DIGITACAO || (nsirStatus === Status.EM_DIGITACAO && (values.tipoServico === 'E' || values.tipoServico === 'EM' || values.tipoServico === 'OS' || values.tipoServico === 'PR'))))}
              />
            </Grid>
            <Grid size={{ xs: 3 }} sx={fieldContainerSx}>
              <InputFormatV2
                error={!!(errors && errors.latitude)}
                helperText={errors && values.latitude ? 'O campo é inválido' : 'Latitude e Longidute ou Coordenada deve ser preenchido'}
                name="latitude"
                label="Latitude"
                value={values.latitude}
                onChange={event => handleChange(event.target.name, event.target.value)}
                disabled={disabledEditDigitador() || (canEditNsmp ? false : nsirStatus !== Status.EM_DIGITACAO)}
                format="##,######"
              />
            </Grid>
            <Grid size={{ xs: 3 }} sx={fieldContainerSx}>
              <InputFormatV2
                error={!!(errors && errors.longitude)}
                helperText={errors && values.longitude ? 'O campo é inválido' : 'Latitude e Longidute ou Coordenada deve ser preenchido'}
                name="longitude"
                label="Longitude"
                value={values.longitude}
                onChange={event => handleChange(event.target.name, event.target.value)}
                disabled={disabledEditDigitador() || (canEditNsmp ? false : nsirStatus !== Status.EM_DIGITACAO)}
                format="##,######"
              />
            </Grid>
            <Grid size={{ xs: 3 }} sx={fieldContainerSx}>
              <InputFormatV2
                error={!!(errors && errors.coordenada)}
                helperText={errors && values.coordenada ? 'O campo é inválido' : 'Latitude e Longidute ou Coordenada deve ser preenchido'}
                name="coordenada"
                label="Absoluta N"
                value={values.coordenada}
                onChange={event => handleChange(event.target.name, event.target.value)}
                disabled={disabledEditDigitador() || (canEditNsmp ? false : nsirStatus !== Status.EM_DIGITACAO)}
                format="######:#######"
              />
            </Grid>
            <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
              <InputV2
                label="Observação do Digitador (outras informações)"
                name="observacaoDigitador"
                onChange={event => handleChange(event.target.name, event.target.value)}
                multiline
                rows={3}
                value={values.observacaoDigitador}
                disabled={disabledEditDigitador() || (canEditNsmp ? false : nsirStatus !== Status.EM_DIGITACAO)}
              />
            </Grid>
          </Grid>
        )}
        <ArquivoContainerV2
          arquivos={values.fotos}
          showDescription={isUsoMutuo(nsirData.tipoServicoId)}
          onSave={fotos => {
            setEditFiles(true);
            setValues({ ...values, fotos });
          }}
          disabled={canEditNsmp ? false : readOnlyLocal}
        />
        <Grid size={{ xs: 12 }} sx={fieldContainerSx}>
          {errors && errors.fotos && (
            <Typography sx={requiredTextSx}>*Imagens são obrigatórias</Typography>
          )}
        </Grid>
      </Grid>
    </DialogDefaultV2>
  );
};

UsoMutuoModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  selectedId: PropTypes.string,
  nsmps: PropTypes.array.isRequired,
  nsmpSelected: PropTypes.object.isRequired,
  loadNsmpById: PropTypes.func.isRequired,
  readOnly: PropTypes.bool.isRequired,
  nsirData: PropTypes.object.isRequired,
  nsirTitle: PropTypes.string.isRequired,
};

UsoMutuoModal.defaultProps = {
  selectedId: undefined,
};

const mapStateToProps = state => ({
  nsmps: selectorsNsmp.getNsmps(state),
  nsmpSelected: selectorsNsmp.getNsmpSelected(state),
  userRole: selectorsAuth.getRole(state),
  authData: selectorsAuth.getAuth(state),
  nsirStatus: selectorsNsir.getNsirStatus(state),
  nsirData: selectorsNsir.getNsirSelected(state),
  userId: selectorsAuth.getUserId(state),
});

const mapDispatchToProps = dispatch => bindActionCreators({
  ...actionsNsmp,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(UsoMutuoModal);
